// Ed25519 verification and the trial verdict — the security core of the demo's
// licensing, with no Firestore or browser dependencies so it can be unit-tested:
//
//     node scripts/test-license.mjs
//
// The License Server signs every payload with its private key; this verifies
// with the embedded public key, both on the live response AND on the copy
// cached in the visitor's own sandbox. The Firestore rules let a visitor write
// anywhere in their sandbox, including this document — which is safe precisely
// because editing it invalidates the signature. Everything here fails CLOSED.

import * as ed from "@noble/ed25519";
// Extension is explicit so Node can load this file directly for
// scripts/test-license.mjs; webpack resolves it the same either way.
import { PLAN } from "./plans.js";

export const PRODUCT_ID = "staff-portal";

// How long a verified licence stays usable with the server unreachable, bounded
// on `signedAt` so a cached document cannot be replayed indefinitely.
export const GRACE_PERIOD_MS = 30 * 86400000;

// Ed25519 public key (raw 32-byte, hex) — safe to embed; verifies, cannot sign.
const LICENSE_PUBKEY_HEX =
  "84b3bc3f5ee2aacb82cd3266cecc069e67140b44192006c744aa20f19a06ad2d";

const hexToBytes = (h) =>
  new Uint8Array(h.match(/.{2}/g).map((b) => parseInt(b, 16)));
const b64ToBytes = (b) => Uint8Array.from(atob(b), (c) => c.charCodeAt(0));

// @noble v2 requires the key as BYTES, not a hex string.
let PUBKEY = hexToBytes(LICENSE_PUBKEY_HEX);

// Lets the test suite verify against a throwaway key pair, so the real
// canonical-JSON and signature path is exercised rather than a stub. Nothing in
// the app calls this.
export function __setPublicKeyForTests(bytes) {
  PUBKEY = bytes;
}

// @noble needs a SHA-512 implementation. Web Crypto's digest('SHA-512') is
// universally available — unlike its Ed25519 primitive, which is missing or
// buggy in many browsers and would silently fail closed.
ed.etc.sha512Async = async (...m) =>
  new Uint8Array(await crypto.subtle.digest("SHA-512", ed.etc.concatBytes(...m)));

// MUST stay byte-identical to canonicalLicense() on the server: same key order,
// same null normalisation, maxUsers appended LAST and only when present (so
// uncapped licences keep the canonical form they had before that field existed).
export function canonicalLicense(p) {
  const o = {
    key: p.key,
    product: p.product,
    plan: p.plan,
    expiresAt: p.expiresAt ?? null,
    customerEmail: p.customerEmail ?? "",
    features: Array.isArray(p.features) && p.features.length ? p.features : null,
    instanceId: p.instanceId ?? "",
    signedAt: p.signedAt,
  };
  if (p.maxUsers != null) o.maxUsers = p.maxUsers;
  return JSON.stringify(o);
}

export async function verifyLicenseSignature(payload) {
  if (!payload?.signature || !payload?.signedAt) return false;
  try {
    const sig = b64ToBytes(payload.signature);
    const msg = new TextEncoder().encode(canonicalLicense(payload));
    return await ed.verifyAsync(sig, msg, PUBKEY);
  } catch {
    return false;
  }
}

const KNOWN_PLANS = [PLAN.FREE, PLAN.DEMO, PLAN.TRIAL, PLAN.PRO];

/**
 * Resolve a payload or cached document into a verdict. Fails closed.
 *
 * `plan` is part of the signed message, so it cannot be edited upwards any more
 * than the expiry can — which is what lets the free tier be enforced in a
 * sandbox the visitor is otherwise free to write to.
 *
 * @param d   the signed licence document, or null
 * @param uid the current visitor's auth uid — the licence is bound to it
 * @returns {{ok, plan?, reason?, expiresAt?, daysLeft?}}
 */
export async function resolveLicense(d, uid) {
  if (!d || !d.key || !d.signature) return { ok: false, reason: "none" };
  if (!(await verifyLicenseSignature(d))) return { ok: false, reason: "unverified" };
  // Anti-replay: a licence minted for someone else must not unlock this session.
  if ((d.instanceId ?? "") !== uid) return { ok: false, reason: "not_yours" };
  if (d.product !== PRODUCT_ID) return { ok: false, reason: "wrong_product" };
  // An unrecognised plan is not a licence we know how to honour.
  if (!KNOWN_PLANS.includes(d.plan)) return { ok: false, reason: "unknown_plan" };

  const signedMs = d.signedAt ? Date.parse(d.signedAt) : 0;
  if (!signedMs || Date.now() - signedMs > GRACE_PERIOD_MS) {
    return { ok: false, reason: "stale", plan: d.plan };
  }
  if (d.expiresAt) {
    const exp = Date.parse(d.expiresAt);
    if (exp && exp < Date.now()) {
      return { ok: false, reason: "expired", plan: d.plan, expiresAt: d.expiresAt };
    }
    return {
      ok: true,
      plan: d.plan,
      expiresAt: d.expiresAt,
      daysLeft: Math.max(0, Math.ceil((exp - Date.now()) / 86400000)),
    };
  }
  return { ok: true, plan: d.plan };
}
